import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, CheckCircle2, Lightbulb } from 'lucide-react';
import type { WorkflowTemplate, SetupGuideStep } from './workflowTemplates';

interface Props {
  template: WorkflowTemplate;
  templateName: string;
  onClose: () => void;
}

export const TemplateSetupGuideModal: React.FC<Props> = ({ template, templateName, onClose }) => {
  const guide = template.setupGuide ?? [];
  const [activeStep, setActiveStep] = useState(0);

  if (guide.length === 0) return null;

  const current: SetupGuideStep = guide[activeStep];
  const isLast = activeStep === guide.length - 1;
  const isFirst = activeStep === 0;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col"
        style={{ width: 520, maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div
          className="flex items-start justify-between px-6 pt-5 pb-4 rounded-t-2xl"
          style={{ background: 'linear-gradient(135deg, #6C63FF 0%, #9B8FFF 100%)' }}
        >
          <div className="flex items-center gap-3">
            <span style={{ fontSize: 28 }}>{template.icon}</span>
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 2 }}>
                Hướng dẫn cấu hình Template
              </p>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: 'white', margin: 0 }}>
                {templateName}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors"
            style={{ padding: '4px', marginTop: -2 }}
          >
            <X size={18} color="white" />
          </button>
        </div>

        {/* ── Step tabs ── */}
        <div className="flex items-center gap-1 px-6 pt-4 pb-1 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {guide.map((s, idx) => {
            const done = idx < activeStep;
            const active = idx === activeStep;
            return (
              <button
                key={idx}
                onClick={() => setActiveStep(idx)}
                className="flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                style={{
                  background: active ? '#6C63FF' : done ? '#EEF2FF' : '#F3F4F6',
                  color: active ? 'white' : done ? '#6C63FF' : '#6B7280',
                  border: active ? '2px solid #6C63FF' : done ? '2px solid #C7D2FE' : '2px solid transparent',
                  flexShrink: 0,
                }}
              >
                {done ? <CheckCircle2 size={12} /> : null}
                <span>{s.step}</span>
              </button>
            );
          })}
        </div>

        {/* ── Step content ── */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Node header */}
          <div className="flex items-center gap-3 mb-4">
            <div
              className="flex items-center justify-center rounded-xl flex-shrink-0"
              style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #EEF2FF, #E0E7FF)', fontSize: 22 }}
            >
              {current.icon}
            </div>
            <div>
              <p style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', margin: 0 }}>
                {current.step}
              </p>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', margin: '2px 0 0' }}>
                {current.nodeLabel}
              </h3>
            </div>
          </div>

          {/* Instructions */}
          <ul className="flex flex-col gap-2.5" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {current.instructions.map((inst, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span
                  className="flex-shrink-0 flex items-center justify-center rounded-full text-white font-bold"
                  style={{
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    fontSize: 10,
                    marginTop: 2,
                    background: '#6C63FF',
                  }}
                >
                  {i + 1}
                </span>
                <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.6, margin: 0 }}>{inst}</p>
              </li>
            ))}
          </ul>

          {/* Tip */}
          {current.tip && (
            <div
              className="flex items-start gap-3 mt-4 rounded-xl p-3"
              style={{ background: '#FFFBEB', border: '1.5px solid #FDE68A' }}
            >
              <Lightbulb size={16} color="#D97706" style={{ flexShrink: 0, marginTop: 2 }} />
              <p style={{ fontSize: 12.5, color: '#92400E', margin: 0, lineHeight: 1.6 }}>
                <strong>Lưu ý: </strong>{current.tip}
              </p>
            </div>
          )}
        </div>

        {/* ── Reminder note ── */}
        <div
          className="mx-6 mb-1 flex items-center gap-2 rounded-lg px-3 py-2"
          style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}
        >
          <span style={{ fontSize: 13, flexShrink: 0 }}>💡</span>
          <p style={{ fontSize: 11.5, color: '#15803D', margin: 0, lineHeight: 1.5 }}>
            Hãy vào biểu tượng <strong>?</strong> trên thẻ template để mở lại setup guide nếu bạn quên cách cấu hình.
          </p>
        </div>

        {/* ── Footer navigation ── */}
        <div
          className="flex items-center justify-between px-6 py-4 rounded-b-2xl"
          style={{ borderTop: '1px solid #F3F4F6' }}
        >
          {/* Progress dots */}
          <div className="flex items-center gap-1.5">
            {guide.map((_, idx) => (
              <div
                key={idx}
                onClick={() => setActiveStep(idx)}
                style={{
                  width: idx === activeStep ? 20 : 6,
                  height: 6,
                  borderRadius: 999,
                  background: idx === activeStep ? '#6C63FF' : idx < activeStep ? '#A5B4FC' : '#E5E7EB',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                onClick={() => setActiveStep((s) => s - 1)}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                style={{ color: '#6B7280', background: '#F3F4F6', border: 'none', cursor: 'pointer' }}
              >
                <ChevronLeft size={14} />
                Trước
              </button>
            )}
            {isLast ? (
              <button
                onClick={onClose}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                style={{ background: '#6C63FF', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                <CheckCircle2 size={14} />
                Đã hiểu, bắt đầu cấu hình
              </button>
            ) : (
              <button
                onClick={() => setActiveStep((s) => s + 1)}
                className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                style={{ background: '#6C63FF', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                Tiếp theo
                <ChevronRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
